import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Components/Layout/Layout';
import {
  Box,
  Typography,
  Table, TableHead, TableBody, TableRow, TableCell,
  Button,
  TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem,
  Autocomplete,
  Checkbox, FormControlLabel,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getToken = () => localStorage.getItem('auth_token') || '';

// Cookies
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop()) : null;
};

export default function DepartmentList() {
  const navigate = useNavigate();

  // Data state
  const [departments, setDepartments] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [schedules, setSchedules] = useState([]); // schedules
  const [loading, setLoading] = useState(true);

  // UI state
  const [dlgCreate, setDlgCreate] = useState(false);
  const [dlgEdit, setDlgEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [locExc, setLocExc] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [respSel, setRespSel] = useState([]);
  const [locSel, setLocSel] = useState([]);

  // Toggle: show deactivated users in selectors?
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => {
    const v = getCookie('show_inactive_users');
    setShowInactive(v === '1');
  }, []);
  const toggleShowInactive = (e) => {
    const v = e.target.checked;
    setShowInactive(v);
    setCookie('show_inactive_users', v ? '1' : '0');
  };

  // Utility helpers
  const orgName = (id) =>
    orgs.find((o) => String(o.id) === String(id))?.name || id || '-';
  const empLabel = (u) => `${u.first_name} ${u.last_name}`.trim();
  const locLabel = (o) => o.label;

  // Generic parser for CSV -> objects (используем для locations)
  const parseSel = (str, src) =>
    str
      ? str
          .split(',')
          .map((s) => s.trim())
          .map((id) => src.find((o) => String(o.id) === id))
          .filter(Boolean)
      : [];

  // ===== Employee options (учитываем is_active / active) =====
  const employeeOptions = useMemo(() => {
    if (showInactive) return employees;
    return employees.filter((u) => {
      const flag = u.is_active ?? u.active;
      return flag === undefined ? true : Boolean(flag);
    });
  }, [employees, showInactive]);

  // Сохраняем выбранных даже если они сейчас не проходят фильтр
  const employeeOptionsWithSelected = useMemo(() => {
    const setIds = new Set(employeeOptions.map((u) => String(u.id)));
    const extras = respSel.filter((u) => !setIds.has(String(u.id)));
    return [...employeeOptions, ...extras];
  }, [employeeOptions, respSel]);

  // ===== Fetch data =====
  const fetchDeps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/departments?auth_token=${getToken()}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setDepartments(data.departments || []);
      } else {
        console.error('Failed to fetch departments:', data.message);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/admin/organizations?auth_token=${getToken()}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setOrgs(data.organizations || []);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/admin/users?auth_token=${getToken()}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setEmployees(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/allowed-locations`);
      const data = await res.json();
      const pts = (data.points || []).map((p) => ({
        id: `P${p.id}`,
        label: `📍 ${p.name || `Zone #${p.id}`}`,
      }));
      const polys = (data.polygons || []).map((a) => ({
        id: `A${a.id}`,
        label: `🔷 ${a.name || `Area #${a.id}`}`,
      }));
      setLocations([...pts, ...polys]);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(
        `${API_URL}/admin/schedules?auth_token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setSchedules(data.schedules || []);
      }
    } catch (err) {
      console.error('Error fetching schedules:', err);
    }
  }, []);

  useEffect(() => {
    fetchDeps();
    fetchOrgs();
    fetchEmployees();
    fetchLocations();
    fetchSchedules();
  }, [fetchDeps, fetchOrgs, fetchEmployees, fetchLocations, fetchSchedules]);

  // ===== Helpers для responsibles / locations на вывод =====

  // Как в TeamList/OrganizationList — понимает id ИЛИ chat_id
  const parseEmployeesToNames = (csv) => {
    if (!csv) return [];
    const tokens = csv.split(',').map((s) => s.trim()).filter(Boolean);
    return tokens
      .map((tok) => {
        let emp = employees.find((e) => String(e.id) === tok);
        if (!emp) emp = employees.find((e) => String(e.chat_id) === tok);
        return emp ? empLabel(emp) : tok;
      })
      .filter(Boolean);
  };

  const parseLocationsToNames = (csv) => {
    if (!csv) return [];
    const tokens = csv.split(',').map((s) => s.trim()).filter(Boolean);
    return tokens
      .map((id) => {
        const loc = locations.find((o) => String(o.id) === id);
        return loc ? loc.label.replace(/^.\s/, '') : id;
      })
      .filter(Boolean);
  };

  const shortList = (arr) =>
    arr.length > 3 ? `${arr.slice(0, 3).join(', ')} …` : arr.join(', ');

  // ===== Form handling =====
  const openCreate = () => {
    setName('');
    setOrgId('');
    setLocExc(false);
    setSelectedSchedules([]);
    setRespSel([]);
    setLocSel([]);
    setDlgCreate(true);
  };

  const openEdit = (dep) => {
    setEditing(dep);
    setName(dep.name || '');
    setOrgId(dep.org_id || '');
    setLocExc(!!dep.location_exception);

    // schedules
    const depSchedules = dep.schedules || [];
    setSelectedSchedules(
      depSchedules
        .map((s) =>
          schedules.find((sched) => sched.schedule_id === s.schedule_id)
        )
        .filter(Boolean)
    );

    // responsibles — по id ИЛИ chat_id из CSV
    const toSelEmployees = (csv, opts) => {
      if (!csv) return [];
      const tokens = csv.split(',').map((i) => i.trim()).filter(Boolean);
      return tokens
        .map((tok) => {
          let u = opts.find((o) => String(o.id) === tok);
          if (!u) u = opts.find((o) => String(o.chat_id) === tok);
          return u || null;
        })
        .filter(Boolean);
    };
    setRespSel(toSelEmployees(dep.responsibles, employees));

    // locations — по id
    setLocSel(parseSel(dep.allowed_locations, locations));
    setDlgEdit(true);
  };

  const payload = () => ({
    name,
    org_id: orgId,
    auth_token: getToken(),
    location_exception: locExc,
    start_times: selectedSchedules.map((s) => s.schedule_id).join(','), // schedule IDs
    responsibles: respSel.map((u) => u.id).join(','),                   // только ID
    allowed_locations: locSel.map((o) => o.id).join(','),
  });

  const saveCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchDeps();
        setDlgCreate(false);
      } else {
        console.error('Create department error:', data.message);
      }
    } catch (err) {
      console.error('Network error:', err);
    }
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/departments/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchDeps();
        setDlgEdit(false);
        setEditing(null);
      } else {
        console.error('Edit department error:', data.message);
      }
    } catch (err) {
      console.error('Network error:', err);
    }
  };

  const deleteDep = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      const res = await fetch(
        `${API_URL}/admin/departments/${id}?auth_token=${getToken()}`,
        {
          method: 'DELETE',
        }
      );
      const data = await res.json();
      if (data.status === 'success') {
        fetchDeps();
      } else {
        console.error('Delete department error:', data.message);
      }
    } catch (err) {
      console.error('Network error:', err);
    }
  };

  const userRole = localStorage.getItem('userRole') || '';

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Departments</Typography>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox checked={showInactive} onChange={toggleShowInactive} />
            }
            label="Show deactivated users (affects selectors)"
          />
        </Box>

        {['ceo', 'admin'].includes(userRole.toLowerCase()) && (
          <Button variant="contained" sx={{ mt: 2 }} onClick={openCreate}>
            Create Department
          </Button>
        )}

        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading…</Typography>
        ) : (
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Loc Exception</TableCell>
                <TableCell>Schedules</TableCell>
                <TableCell>Responsibles</TableCell>
                <TableCell>Allowed Locations</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((dep) => {
                const respNames = parseEmployeesToNames(dep.responsibles);
                const locNames = parseLocationsToNames(dep.allowed_locations);
                return (
                  <TableRow key={dep.id}>
                    <TableCell>{dep.id}</TableCell>
                    <TableCell>{dep.name}</TableCell>
                    <TableCell>{orgName(dep.org_id)}</TableCell>
                    <TableCell>{dep.location_exception ? 'Да' : 'Нет'}</TableCell>
                    <TableCell>
                      {(dep.schedules || []).map((s) => (
                        <Chip
                          key={s.schedule_id}
                          label={`${s.start_times} (${s.days_of_week})`}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {respNames.length ? shortList(respNames) : '-'}
                    </TableCell>
                    <TableCell>
                      {locNames.length ? shortList(locNames) : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/admin/entity_timesheet_detail/department/${dep.id}`
                          )
                        }
                        sx={{ mr: 1 }}
                      >
                        <VisibilityIcon fontSize="small" /> View
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEdit(dep)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteDep(dep.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Create dialog */}
        <Dialog
          open={dlgCreate}
          onClose={() => setDlgCreate(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create Department</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              sx={{ mt: 2 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              fullWidth
              sx={{ mt: 2 }}
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select organization</em>
              </MenuItem>
              {orgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={locExc}
                  onChange={(e) => setLocExc(e.target.checked)}
                />
              }
              label="Исключение локации"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) =>
                `${o.start_times.replace(',', '-')} (${o.days_of_week})`
              }
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => (
                <TextField {...params} label="Schedules" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={employeeOptionsWithSelected}
              getOptionLabel={empLabel}
              value={respSel}
              onChange={(e, v) => setRespSel(v)}
              isOptionEqualToValue={(opt, val) =>
                String(opt.id) === String(val.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Responsibles" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={locations}
              getOptionLabel={locLabel}
              value={locSel}
              onChange={(e, v) => setLocSel(v)}
              renderInput={(params) => (
                <TextField {...params} label="Allowed locations" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDlgCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveCreate}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog
          open={dlgEdit}
          onClose={() => setDlgEdit(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Edit Department</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              sx={{ mt: 2 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              fullWidth
              sx={{ mt: 2 }}
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select organization</em>
              </MenuItem>
              {orgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={locExc}
                  onChange={(e) => setLocExc(e.target.checked)}
                />
              }
              label="Исключение локации"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) =>
                `${o.start_times.replace(',', '-')} (${o.days_of_week})`
              }
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => (
                <TextField {...params} label="Schedules" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={employeeOptionsWithSelected}
              getOptionLabel={empLabel}
              value={respSel}
              onChange={(e, v) => setRespSel(v)}
              isOptionEqualToValue={(opt, val) =>
                String(opt.id) === String(val.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Responsibles" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={locations}
              getOptionLabel={locLabel}
              value={locSel}
              onChange={(e, v) => setLocSel(v)}
              renderInput={(params) => (
                <TextField {...params} label="Allowed locations" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDlgEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit}>
              Save changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
